import React from 'react'
import { connect } from 'react-redux'
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Tab
} from '@mui/material'
import { TabContext, TabList } from '@mui/lab'
import update from 'immutability-helper'
import deepEqual from 'fast-deep-equal'
import { initRule } from '../../lib/rule'
import { sendWs, RULE } from '../../lib/send'
import { rxOpenRule } from '../../lib/rx_subject'
import FadeTabPanel from '../../components/FadeTabPanel'
import FormWithButton from '../../components/FormWithButton'
import PresetRule from './PresetRule'
import RightRule from './RightRule'
import PlayerRule from './PlayerRule'
import TeamRule from './TeamRule'
import BoardRule from './BoardRule'
import OtherRule from './OtherRule'
import './Rule.scss'

const kinds = ['right', 'player', 'team', 'board', 'other']

const Rule = ({ rule }) => {
  const [open, setOpen] = React.useState(false)
  const [tab, setTab] = React.useState('preset')
  const [form, setForm] = React.useState(initRule)

  const formEls = {}
  kinds.forEach(kind => {
    formEls[kind] = React.createRef()
  })

  React.useEffect(() => {
    const subscription = rxOpenRule.subscribe(() => {
      setOpen(true)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  React.useEffect(() => {
    if (form.board.active) {
      setForm(update(form, {
        player: {
          specialCorrect: {
            consBonus: { $set: false },
            passQuiz: { $set: false },
            survival: { active: { $set: false } }
          }
        }
      }))
    }

    if (!form.player.comprehensive.active &&
        (form.other.winLoseOrder === 'comp-point' ||
         form.other.winLoseOrder === 'comp-point-and-point')) {
      setForm(update(form, {
        other: {
          winLoseOrder: { $set: 'point' }
        }
      }))
    }
  }, [form])

  const onEnter = () => {
    setTab('preset')
    setForm(rule)
  }

  const changeTab = (evt, newTab) => {
    if (formEls[tab] && formEls[tab].current && !formEls[tab].current.reportValidity()) {
      return
    }

    setTab(newTab)
  }

  const updateRule = key => updator => {
    setForm(form => update(form, { [key]: { $set: updator(form[key]) } }))
  }

  const setPresetRule = newRule => {
    sendWs(RULE, update(newRule, {
      right: { $set: rule.right },
      other: {
        showWinTimes: { $set: rule.other.showWinTimes },
        winLoseOrder: { $set: rule.other.winLoseOrder }
      }
    }))
    setOpen(false)
  }

  const clearPreset = () => {
    const formCopy = update(form, {
      right: { $set: rule.right },
      other: {
        showWinTimes: { $set: rule.other.showWinTimes },
        winLoseOrder: { $set: rule.other.winLoseOrder }
      },
      preset: { $set: rule.preset }
    })

    return !deepEqual(formCopy, rule)
  }

  const submit = evt => {
    evt.preventDefault()

    if (formEls[tab] && formEls[tab].current && !formEls[tab].current.reportValidity()) {
      return
    }

    setOpen(false)
    const newRule = update(form, {
      preset: { $set: !clearPreset() ? form.preset : null }
    })
    sendWs(RULE, newRule)
  }

  const close = () => {
    setOpen(false)
  }

  return (
    <Dialog className="rule-dialog" open={open}
            TransitionProps={{ onEnter }}
            aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">ルール</DialogTitle>
      <DialogContent className="rule">
        <TabContext value={tab}>
          <TabList onChange={changeTab}>
            <Tab className="preset-rule-tab" value="preset" label="プリセット" />
            <Tab className="right-rule-tab" value="right" label="解答権" />
            <Tab className="player-rule-tab" value="normal" label="個人" />
            <Tab className="team-rule-tab" value="team" label="チーム" />
            <Tab className="board-rule-tab" value="board" label="ボード" />
            <Tab className="other-rule-tab" value="other" label="その他" />
          </TabList>
          <FadeTabPanel tab={tab} value="preset" className="preset-rule">
            <PresetRule rule={form} setRule={setPresetRule} />
          </FadeTabPanel>
          <FadeTabPanel tab={tab} value="right" className="right-rule">
            <FormWithButton ref={formEls.right} onSubmit={submit}>
              <RightRule rule={form.right} changeRule={updateRule('right')}
                         entireRule={form} />
            </FormWithButton>
          </FadeTabPanel>
          <FadeTabPanel tab={tab} value="normal" className="player-rule">
            <FormWithButton ref={formEls.player} onSubmit={submit}>
              <PlayerRule rule={form.player} changeRule={updateRule('player')}
                          entireRule={form} />
            </FormWithButton>
          </FadeTabPanel>
          <FadeTabPanel tab={tab} value="team" className="team-rule">
            <FormWithButton ref={formEls.team} onSubmit={submit}>
              <TeamRule rule={form.team} changeRule={updateRule('team')}
                        entireRule={form} />
            </FormWithButton>
          </FadeTabPanel>
          <FadeTabPanel tab={tab} value="board" className="board-rule">
            <FormWithButton ref={formEls.board} onSubmit={submit}>
              <BoardRule rule={form.board} changeRule={updateRule('board')}
                         entireRule={form} />
            </FormWithButton>
          </FadeTabPanel>
          <FadeTabPanel tab={tab} value="other" className="other-rule">
            <FormWithButton ref={formEls.other} onSubmit={submit}>
              <OtherRule rule={form.other} changeRule={updateRule('other')}
                         entireRule={form} />
            </FormWithButton>
          </FadeTabPanel>
        </TabContext>
      </DialogContent>
      <DialogActions className="dialog-actions">
        <form onSubmit={submit}>
          <Button type="submit" className="submit" color="primary"
                  disabled={tab === 'preset'}>
            設定
          </Button>
          <Button className="close" color="secondary" onClick={close}>
            閉じる
          </Button>
        </form>
      </DialogActions>
    </Dialog>
  )
}

export default connect(
  state => ({
    rule: state.rule
  })
)(Rule)
